import type { ChessMove, ChessView } from "@bing/engine-chess";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DifficultySelect } from "../components/DifficultySelect";
import { StartRow, StartScreenShell } from "../components/StartScreenShell";
import { ChessPiece } from "../screens/ChessTable";
import { getChessSide, setChessSide } from "../settings/prefs";
import { MatchEndCard, namedMember } from "../components/MatchEndCard";
import { faDigits } from "../i18n/digits";
import type { SeatIdentity } from "./seat-identity";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import { ChessTable } from "../screens/ChessTable";
import { getChessDifficulty, setChessDifficulty } from "../settings/prefs";
import type { BotDifficulty } from "@bing/shared";
import { useSession } from "../store/context";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { useEndReveal } from "./use-end-reveal";
import type { GameResult } from "./connection";
import { takeResumeIntent } from "./fresh-solo";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";

/** The per-player end-of-game overlay — chess has THREE outcomes (win / lose /
 * draw), so it's modelled on the individual-game overlays (Hearts/Bluff), NOT
 * Hokm's team scoreboard: a dimmed table, the result, the on-board reason
 * (checkmate / stalemate / the draw kind), and play-again / exit. Exported so a
 * future online chess game can reuse the exact same end card. */
export function ChessEndOverlay({
  view,
  result,
  self,
  opponent,
  opponentAbsent,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  view: ChessView;
  result: GameResult;
  /** card-98 item 12: the two players' rows — the same identities the board's strips get (yours: the
   *  signed-in profile; the opponent: a resolved human online, else none — «ربات»). */
  self?: SeatIdentity | null;
  opponent?: SeatIdentity | null;
  /** The other chair holds NOBODY (a resumed game — `absentSeats`): with no name resolved, it reads
   *  «حریف», as the board does — never «ربات», which is only true once a bot sits down (E-I3). */
  opponentAbsent?: boolean;
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const draw = result.winners.length === 0;
  const won = result.winners.includes(view.you);
  const title = draw ? fa.chess.drawResult : won ? fa.chess.youWin : fa.chess.youLose;
  // The ROOM's reason wins when the BOARD has none: a forfeit / flag-fall / claim leaves
  // `view.status` at «playing», so without this a win by an opponent walking out showed a bare
  // «بردی!» and nothing else — the one result that most needs explaining.
  const roomReason =
    result.reason === "resign"
      ? fa.chess.endResign
      : result.reason === "timeout"
        ? fa.chess.endTimeout
        : result.reason === "claim"
          ? fa.chess.endClaim
          : /* A SEAT THE TABLE REMOVED FOR LAPSED TURNS — see `fa.chess.endIdle`: unreachable on
               شطرنج today (it is on no branch of `chargeIdleMiss`'s `removable`), carried because
               `ChessEndReason` does. Side-aware, unlike `resign`: a resigner has left and never
               reads their own card, a removed player is still at the board. */
            result.reason === "idle"
            ? won
              ? fa.chess.endIdle
              : fa.chess.endIdleLost
            : "";
  const boardReason =
    view.status === "checkmate"
      ? fa.chess.checkmate
      : view.status === "stalemate"
        ? fa.chess.stalemate
        : view.status === "draw"
          ? view.drawReason === "50-move"
            ? fa.chess.drawFifty
            : view.drawReason === "threefold"
              ? fa.chess.drawThreefold
              : view.drawReason === "insufficient"
                ? fa.chess.drawInsufficient
                : fa.chess.draw
          : "";
  const reason = boardReason || roomReason;
  // card-98 item 12: the shared template — the winning feel, each PLAYER a side (as شلم draws a team):
  // face, name and chess's own point (۱ · ۰, ½ each for a draw), the winner first
  const opp = 1 - view.you;
  const order = draw || won ? [view.you, opp] : [opp, view.you];
  const point = (seat: number): string =>
    draw ? "½" : result.winners.includes(seat) ? faDigits(1) : faDigits(0);
  return (
    <MatchEndCard
      label={fa.chess.gameOver}
      outcome={draw ? "drawn" : won ? "won" : "lost"}
      glyph="♟️"
      title={title}
      reason={reason || undefined}
      sides={order.map((seat) => ({
        key: `seat-${seat}`,
        mine: seat === view.you,
        members: [
          seat === view.you
            ? namedMember(seat, self, fa.chess.you, "fox")
            : namedMember(
                seat,
                opponent,
                opponentAbsent ? fa.chess.awayName : fa.chess.opponent,
                "wolf",
              ),
        ],
        result: point(seat),
        attrs: { "data-seat": seat },
      }))}
      promo
      playAgain={fa.chess.playAgain}
      leave={fa.chess.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/** The pre-game START screen: the player picks the opponent level ONCE here, then it is LOCKED
 * for the whole game (no mid-game picker). «بازی دوباره» returns here, so every game begins with a
 * deliberate level choice — the operator's «سطح حریف at the start, not changeable mid-game» rule. */
/**
 * ITEM 7 — the offline piece-colour picker.
 *
 * Chess had no colour choice at all offline: you were always White. The side is the human's
 * SEAT (0 = White, 1 = Black), so this is a real gameplay choice — White moves first — not a
 * skin. Swatches show the actual piece glyph in each colour rather than the words alone.
 */
function ChessSideSelect({
  value,
  onChange,
}: {
  value: 0 | 1;
  onChange: (side: 0 | 1) => void;
}): ReactNode {
  const swatch = (side: 0 | 1): ReactNode => {
    const on = value === side;
    const label = side === 0 ? fa.chess.colorWhite : fa.chess.colorBlack;
    return (
      <button
        key={side}
        type="button"
        role="radio"
        aria-checked={on}
        aria-label={label}
        data-testid={`chess-side-${side}`}
        onClick={() => onChange(side)}
        style={{
          appearance: "none",
          margin: 0,
          cursor: "pointer",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "8px 12px",
          borderRadius: 12,
          border: on
            ? "1px solid rgb(var(--c-secondary))"
            : "1px solid rgb(var(--c-outline-variant))",
          background: on
            ? "rgb(var(--c-secondary-container) / .4)"
            : "rgb(var(--c-surface-container-low))",
          boxShadow: on ? "0 0 10px rgb(var(--c-secondary) / .35)" : "none",
        }}
      >
        <ChessPiece type="k" color={side === 0 ? "w" : "b"} size={26} />
        <span
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: on ? "rgb(var(--c-secondary))" : "rgb(var(--c-on-surface-variant))",
          }}
        >
          {label}
        </span>
      </button>
    );
  };
  return (
    <div
      role="radiogroup"
      aria-label={fa.chess.color}
      style={{ display: "flex", gap: 8, width: "100%" }}
    >
      {swatch(0)}
      {swatch(1)}
    </div>
  );
}

function ChessStartScreen({
  side,
  onSideChange,
  difficulty,
  onChange,
  onStart,
  onLeave,
}: {
  difficulty: BotDifficulty;
  onChange: (level: BotDifficulty) => void;
  /** ITEM 7 — the side (= seat) the player takes: 0 = White, 1 = Black. */
  side: 0 | 1;
  onSideChange: (side: 0 | 1) => void;
  onStart: () => void;
  onLeave: () => void;
}): ReactNode {
  return (
    <StartScreenShell
      title={fa.chess.title}
      emoji="♟️"
      heading={fa.chess.chooseDifficulty}
      leaveLabel={fa.chess.leave}
      startLabel={fa.chess.startGame}
      onStart={onStart}
      onLeave={onLeave}
    >
      <DifficultySelect
        value={difficulty}
        onChange={onChange}
        ariaLabel={fa.chess.difficulty}
        compact
      />
      {/* ITEM 7 — pick your colour before the game, next to the difficulty. */}
      <StartRow label={fa.chess.color}>
        <ChessSideSelect value={side} onChange={onSideChange} />
      </StartRow>
    </StartScreenShell>
  );
}

/** Owns the solo chess connection + store for ONE game at a FIXED (locked) difficulty. Rides the
 * SAME generic paced solo path as Hokm/بی‌دل (`makeSoloRoom` ↔ `createGameStore`): the same
 * `{view, legal}` frames + two-option «خروج»/«پایان بازی» exit. The difficulty is chosen on the
 * start screen and never changes here — «بازی دوباره» goes back to the start screen (`onAgain`). */
function ChessSoloRound({
  side,
  difficulty,
  onAgain,
  onExit,
}: {
  difficulty: BotDifficulty;
  /** ITEM 7 — the human's seat: 0 = White, 1 = Black. Fixed for this game. */
  side: 0 | 1;
  onAgain: () => void;
  onExit: () => void;
}): ReactNode {
  const initData = useSession((s) => s.initDataRaw);
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endFailed, setEndFailed] = useState(false);

  // Bumped by the channel gate's «عضو شدم» to re-attempt the join (a fresh store).
  const [round, setRound] = useState(0);
  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<ChessMove, ChessView>({
          wsUrl: GAME_SERVER_URL,
          room: "chess",
          getInitData: () => initData,
          difficulty, // FIXED for this game — chosen on the start screen, never changes mid-game
          side, // ITEM 7 — likewise fixed: the seat the human takes (0 = White, 1 = Black)
        }),
      ),
    [initData, round, difficulty, side],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  // Hold the summary overlay for a beat so the final move animates first.
  const showEnd = useEndReveal(result !== null);
  // The win/lose chime lands WITH the summary. `result.winners` is [winner] on a
  // checkmate and [] on a draw (per the engine's score()), so it drives the cue.
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  // The app-root reconnect overlay owns the screen while it's up — don't ALSO
  // paint this container's raw-error screen behind it.
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      onExit();
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  const { loading, progress } = useSoloLoad(view != null, ready, store);

  // «خروج» — leave but KEEP the game resumable (a plain disconnect no longer ends it).
  const doLeave = (): void => {
    store.getState().disconnect();
    onExit();
  };
  // «پایان بازی» — END the game server-side and WAIT for the server's ack before
  // dropping it locally and leaving; on failure stay in the game and offer a retry.
  const doEnd = (): void => {
    if (ending) return;
    setEnding(true);
    setEndFailed(false);
    void store
      .getState()
      .endGame()
      .then(() => {
        clearActiveGame("chess"); // off the resume strip right away; Lobby refetch confirms
        doLeave();
      })
      .catch(() => setEndFailed(true))
      .finally(() => setEnding(false));
  };
  const leave = (): void => setConfirmingLeave(true);
  // «بازی دوباره» → back to the START screen so the next game re-chooses the (locked) level.
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.chess.connectionError}
          </p>
          <Button variant="primary" onClick={onExit}>
            {fa.chess.leave}
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !view) {
    return (
      <Boot
        caption={fa.chess.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.chess.leave}
      />
    );
  }

  return (
    <>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. */}
      <ChessTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : leave}
        self={profile}
      />
      {result && showEnd ? (
        <ChessEndOverlay
          result={result}
          view={view}
          self={profile}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            doEnd();
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
      {endFailed ? (
        <ConfirmDialog
          title={fa.confirm.endFailedTitle}
          body={fa.confirm.endFailedBody}
          confirmLabel={fa.confirm.endRetry}
          onConfirm={doEnd}
          onCancel={() => setEndFailed(false)}
        />
      ) : null}
    </>
  );
}

/** Solo chess: the START screen (choose the LOCKED opponent level) → the game. «بازی دوباره»
 * returns to the start screen, so the level is ALWAYS a deliberate start-of-game choice and never
 * changes mid-game (the operator requirement). */
export function ChessGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  // ITEM 3 — an explicit «ادامه» from the lobby skips this picker. Showing it would ask for
  // settings the server is about to ignore in favour of the saved game — the same silent
  // override, one screen later. A fresh start still lands here.
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getChessDifficulty());
  // ITEM 7 — the side the player takes, remembered between games.
  const [side, setSide] = useState<0 | 1>(() => getChessSide());

  if (phase === "select") {
    return (
      <ChessStartScreen
        side={side}
        onSideChange={(next) => {
          setSide(next);
          setChessSide(next);
        }}
        difficulty={difficulty}
        onChange={(level) => {
          setDifficulty(level);
          setChessDifficulty(level); // remember for next time (the picker pre-selects it)
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      />
    );
  }
  return (
    <ChessSoloRound
      difficulty={difficulty}
      side={side}
      onAgain={() => setPhase("select")}
      onExit={() => setView("lobby")}
    />
  );
}
